import { paths } from "@kwp/firestore";
import { deleteDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import type { FirebaseClients } from "./firebase";

/**
 * Web push (architecture §11). The browser subscribes through Firebase Messaging with the
 * environment's VAPID key and our own service worker (`/firebase-messaging-sw.js`), then
 * registers itself under `users/{uid}/devices/{deviceId}`, the one place besides read markers
 * where the client writes (exactly `token`, `platform`, `updatedAt`). The api sends to every
 * registered device and removes the ones FCM reports as gone.
 *
 * On iPhone and iPad, web push works only for the app added to the Home Screen (iOS 16.4+), and
 * the permission prompt must follow a tap: `enablePush` is called only from a click handler.
 */

export const SW_URL = "/firebase-messaging-sw.js";
/** The worker's own scope, so it never controls the app's pages (it has no fetch handler). */
export const SW_SCOPE = "/firebase-cloud-messaging-push-scope";
/** The message the worker posts to a visible window instead of showing a system notification. */
export const FOREGROUND_PUSH = "kwp-push";
/** The message the worker posts when a notification is clicked while the app is open. */
export const OPEN_LINK = "kwp-open";
const DEVICE_KEY = "kwp.pushDeviceId";

export type PushAvailability =
  | "unconfigured" // no VAPID key (or sender/app id) in this environment's config.json
  | "unsupported" // no service worker / Push API / Notification in this browser
  | "ios-needs-install" // iOS Safari tab: add to Home Screen first
  | "denied" // the person blocked notifications for this site
  | "available";

export interface Environment {
  configured: boolean;
  serviceWorker: boolean;
  pushManager: boolean;
  notification: boolean;
  ios: boolean;
  standalone: boolean;
  permission: NotificationPermission | null;
}

/** Decides what the Settings toggle can offer; pure so every branch is tested. */
export function pushAvailability(env: Environment): PushAvailability {
  if (!env.configured) return "unconfigured";
  if (env.ios && !env.standalone) return "ios-needs-install";
  if (!env.serviceWorker || !env.pushManager || !env.notification) return "unsupported";
  if (env.permission === "denied") return "denied";
  return "available";
}

export function isIos(userAgent: string, maxTouchPoints: number): boolean {
  // iPadOS reports a Mac user agent; touch points give it away.
  return /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
}

export function browserEnvironment(configured: boolean): Environment {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  const hasNotification = "Notification" in window;
  return {
    configured,
    serviceWorker: "serviceWorker" in nav,
    pushManager: "PushManager" in window,
    notification: hasNotification,
    ios: isIos(nav.userAgent, nav.maxTouchPoints),
    standalone: window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true,
    permission: hasNotification ? Notification.permission : null,
  };
}

/** A stable id for one FCM token: the device document's id (hex SHA-256, 32 characters). */
export async function deviceIdFor(token: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
}

export function rememberedDeviceId(): string | null {
  try {
    return window.localStorage.getItem(DEVICE_KEY);
  } catch {
    return null;
  }
}

function rememberDeviceId(id: string | null): void {
  try {
    if (id === null) window.localStorage.removeItem(DEVICE_KEY);
    else window.localStorage.setItem(DEVICE_KEY, id);
  } catch {
    // Storage can be unavailable (private mode); the device document is still the truth.
  }
}

async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
  const worker = registration.installing ?? registration.waiting;
  if (registration.active === null && worker !== null) {
    await new Promise<void>((resolve) => {
      worker.addEventListener("statechange", () => {
        if (worker.state === "activated") resolve();
      });
    });
  }
  return registration;
}

async function currentToken(firebase: FirebaseClients, vapidKey: string): Promise<string> {
  const { getMessaging, getToken } = await import("firebase/messaging");
  return getToken(getMessaging(firebase.app), { vapidKey, serviceWorkerRegistration: await activeRegistration() });
}

async function register(firebase: FirebaseClients, uid: string, token: string, platform: "web" | "ios-pwa") {
  const id = await deviceIdFor(token);
  await setDoc(doc(firebase.db, paths.userDevice(uid, id)), { token, platform, updatedAt: serverTimestamp() });
  return id;
}

export class PushPermissionError extends Error {
  override name = "PushPermissionError";
}

/**
 * Asks for permission (call it straight from a click), subscribes, and registers this device.
 * Returns the device id.
 */
export async function enablePush(firebase: FirebaseClients, uid: string, vapidKey: string, platform: "web" | "ios-pwa"): Promise<string> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new PushPermissionError(permission);
  const id = await register(firebase, uid, await currentToken(firebase, vapidKey), platform);
  rememberDeviceId(id);
  return id;
}

/** Unregisters this device and drops its FCM token. */
export async function disablePush(firebase: FirebaseClients, uid: string, deviceId: string): Promise<void> {
  await deleteDoc(doc(firebase.db, paths.userDevice(uid, deviceId)));
  rememberDeviceId(null);
  const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (registration === undefined) return;
  const { deleteToken, getMessaging } = await import("firebase/messaging");
  await deleteToken(getMessaging(firebase.app));
}

/**
 * On start-up, if this device has push on, re-reads the FCM token (it rotates) and moves the
 * registration when it changed. No prompt: it only runs with permission already granted.
 */
export async function refreshPush(firebase: FirebaseClients, uid: string, vapidKey: string, platform: "web" | "ios-pwa"): Promise<void> {
  const previous = rememberedDeviceId();
  if (previous === null || Notification.permission !== "granted") return;
  const id = await register(firebase, uid, await currentToken(firebase, vapidKey), platform);
  if (id !== previous) {
    await deleteDoc(doc(firebase.db, paths.userDevice(uid, previous)));
    rememberDeviceId(id);
  }
}

export type WorkerMessage =
  | { kind: "push"; title: string; body: string; path: string }
  | { kind: "open"; path: string };

/**
 * Reads a message from the push worker: a push to show as a toast, or a notification click to
 * follow. Links become in-app paths; anything malformed or from another origin is ignored.
 */
export function parseWorkerMessage(data: unknown, origin: string): WorkerMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const { type, title, body, link } = data as Record<string, unknown>;
  if (typeof link !== "string") return null;
  let url: URL;
  try {
    url = new URL(link, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  if (type === OPEN_LINK) return { kind: "open", path };
  if (type !== FOREGROUND_PUSH || typeof title !== "string" || typeof body !== "string") return null;
  return { kind: "push", title, body, path };
}
